'use client';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';

import { useTranslate } from 'src/locales';
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { fDateTime } from 'src/utils/format-time';
import { getCommentListForUser } from 'src/services/comment/user-list-api';
import type { CommentOutput } from 'src/types/backend/generated';
import {getThumbnailUrl} from "@/utils/image-utils.ts";

// ----------------------------------------------------------------------

type Props = {
  userId: number;
};

// ----------------------------------------------------------------------

export function UserCommentsList({ userId }: Props) {
  const { t } = useTranslate();
  const [comments, setComments] = useState<CommentOutput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const pageSize = 6;
  const hasMore = from + pageSize < totalCount;
  const hasPrevious = from > 0;

  const fetchComments = async (newFrom: number) => {
    try {
      setIsLoading(true);
      const result = await getCommentListForUser({
        userId,
        size: pageSize,
        from: newFrom,
        sortBy: 'creationDate',
        sortOrder: 'desc',
      });

      if (result) {
        setComments(result.items);
        setTotalCount(result.totalCount);
        setFrom(newFrom);
      }
    } catch (err) {
      console.error('Error fetching user comments:', err);
      setError(err instanceof Error ? err.message : t('page.user.profile.comments.error_loading') as string);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleLoadOlder = () => {
    fetchComments(from + pageSize);
  };

  const handleLoadNewer = () => {
    fetchComments(Math.max(0, from - pageSize));
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('page.user.profile.comments.title')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('page.user.profile.comments.description')}
          </Typography>

          <Stack spacing={3}>
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />

                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="30%" sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="80%" />
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Skeleton variant="text" width={60} />
                    <Skeleton variant="text" width={80} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('page.user.profile.comments.title')}
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (comments.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('page.user.profile.comments.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('page.user.profile.comments.no_comments')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('page.user.profile.comments.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('page.user.profile.comments.description')}
        </Typography>

        <Stack spacing={3}>
          {comments.map((comment) => (
            <Box key={comment.pimcoreCommentId} sx={{ display: 'flex', gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                }}
                src={getThumbnailUrl(comment.userModel?.photo, '270_270') || undefined}
                alt={comment.userModel?.photo?.filename}
              />

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {fDateTime(comment.createdAt * 1000)}
                </Typography>

                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  {comment.content}
                </Typography>

                <Stack direction="row" spacing={2} sx={{ color: 'text.secondary' }}>
                  <Typography variant="caption">
                    👍 {comment.upVoteCount}
                  </Typography>
                  <Typography variant="caption">
                    👎 {comment.downVoteCount}
                  </Typography>
                  {comment.replyCount > 0 && (
                    <Typography variant="caption">
                      💬 {comment.replyCount} {comment.replyCount === 1
                        ? t('page.user.profile.comments.reply')
                        : t('page.user.profile.comments.replies')}
                    </Typography>
                  )}
                </Stack>

                {comment.recipeId && (
                  <Link
                    component={RouterLink}
                    href={comment.recipeUrlKey ? `/${comment.recipeUrlKey}` : paths.recipe.details(comment.recipeId)}
                    variant="caption"
                    sx={{ mt: 1, display: 'inline-block' }}
                  >
                    {t('page.user.profile.comments.see_recipe')} →
                  </Link>
                )}
              </Box>
            </Box>
          ))}
        </Stack>

        {(hasMore || hasPrevious) && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            {hasPrevious && (
              <Button variant="outlined" size="small" onClick={handleLoadNewer}>
                {t('page.user.profile.comments.load_newer')}
              </Button>
            )}
            {hasMore && (
              <Button variant="outlined" size="small" onClick={handleLoadOlder}>
                {t('page.user.profile.comments.load_older')}
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

